import { useState, useRef } from "react";
import { P, FF, inputBase } from "../../styles/tokens";
import { fmt } from "../../utils/format";
import { api } from "../../services/api";
import MoneyInput from "./MoneyInput";
import { useToast } from "../../contexts/ToastContext";

const TIPO_ICON = { efectivo: "💵", "débito": "💳", vales: "🎫" };

export default function PagarTDCSection({ deudaTDC, cuentas, saldoEfectivo, saldoDebito, setTransferencias, computedSaldos }) {
  const [open, setOpen] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [origen, setOrigen] = useState("");
  const [monto, setMonto] = useState("");
  const [saving, setSaving] = useState(false);
  const [saldoAlert, setSaldoAlert] = useState(null);
  const [payGastos, setPayGastos] = useState(true);
  const [payMsi, setPayMsi] = useState(true);
  const mountedRef = useRef(true);
  const toast = useToast();

  if (!deudaTDC || deudaTDC.length === 0) return null;

  const disponible = saldoEfectivo + saldoDebito;
  const origenCuentas = cuentas.filter(c => c.tipo === "débito" || c.tipo === "efectivo");

  const startPay = (tdc) => {
    if (payingId === tdc.id) { setPayingId(null); return; }
    setPayingId(tdc.id);
    const hasGastos = (tdc.gastosNormales || 0) > 0;
    const hasMsi = (tdc.msiPendiente || 0) > 0;
    setPayGastos(hasGastos);
    setPayMsi(hasMsi);
    let montoInicial = 0;
    if (hasGastos) montoInicial += tdc.gastosNormales;
    if (hasMsi) montoInicial += tdc.msiPendiente;
    setMonto(String(montoInicial || tdc.totalDeuda));
    setOrigen(origenCuentas.length === 1 ? origenCuentas[0].id : "");
  };

  const recalcMonto = (tdc, gastos, msi) => {
    let total = 0;
    if (gastos) total += (tdc.gastosNormales || 0);
    if (msi) total += (tdc.msiPendiente || 0);
    setMonto(String(total));
  };

  const handlePay = async (tdcId) => {
    if (!origen || !Number(monto) || saving) return;
    if (computedSaldos) {
      const saldo = computedSaldos[origen] || 0;
      if (Number(monto) > saldo) {
        setSaldoAlert(`Saldo insuficiente (${fmt(saldo)})`);
        setTimeout(() => setSaldoAlert(null), 3000);
        return;
      }
    }
    setSaving(true);
    try {
      const concepto = payGastos && payMsi ? "Pago TDC"
        : payGastos ? "Pago TDC - Gastos"
        : payMsi ? "Pago TDC - MSI"
        : "Pago TDC";
      const res = await api.transferencias.create({
        cuenta_origen: origen,
        cuenta_destino: tdcId,
        monto: Number(monto),
        concepto,
      });
      const newTransfer = Array.isArray(res) ? res[0] : res;
      // Limpiar UI y actualizar datos en un solo batch
      setPayingId(null);
      setOrigen("");
      setMonto("");
      setSaving(false);
      if (newTransfer) setTransferencias(prev => [...(prev || []), newTransfer]);
      toast.show("✓ Pago registrado");
    } catch (err) {
      console.error("TDC payment failed:", err);
      setSaving(false);
    }
  };

  return (
    <div style={{ marginBottom: 14, borderRadius: 16, border: `1.5px solid #FDE68A`, overflow: "hidden", background: P.surface }}>
      <button type="button"
        onClick={() => setOpen(!open)}
        style={{ width: "100%", padding: "14px 20px", background: "#FFFBEB", display: "flex", justifyContent: "space-between", alignItems: "center", border: "none", cursor: "pointer", fontFamily: FF, borderBottom: open ? `1.5px solid #FDE68A` : "none" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>💳</span>
          <span style={{ fontWeight: 700, fontSize: 14, color: "#92400E" }}>Pagar TDC</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "#92400E", fontFamily: FF }}>{fmt(deudaTDC.reduce((s, t) => s + t.totalDeuda, 0))}</span>
          <span style={{ color: "#B45309", fontSize: 11 }}>{open ? "▲" : "▼"}</span>
        </div>
      </button>

      {open && (
        <div style={{ padding: "8px 16px 16px" }}>
          <div style={{ display: "flex", gap: 10, padding: "8px 0 12px", borderBottom: `1px solid ${P.border}`, marginBottom: 8 }}>
            <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>💵 Efectivo: <strong style={{ color: P.text }}>{fmt(saldoEfectivo)}</strong></span>
            <span style={{ fontSize: 10, color: P.textXs, fontFamily: FF }}>💳 Débito: <strong style={{ color: P.text }}>{fmt(saldoDebito)}</strong></span>
          </div>

          {deudaTDC.map(tdc => {
            const isPaying = payingId === tdc.id;
            const alcanza = disponible >= tdc.totalDeuda;
            return (
              <div key={tdc.id} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: isPaying ? "#FEF3C7" : P.bg, borderRadius: 12, border: `1.5px solid ${isPaying ? "#F59E0B" : P.border}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tdc.nombre}{tdc.banco ? ` · ${tdc.banco}` : ""}</div>
                    <div style={{ fontSize: 10, color: "#B45309", fontFamily: FF, marginTop: 2 }}>
                      {tdc.nGastos} gasto{tdc.nGastos !== 1 ? "s" : ""}{tdc.nMsi > 0 ? ` + ${tdc.nMsi} MSI` : ""}{tdc.fechaLimitePago ? ` · Pagar antes del día ${tdc.fechaLimitePago}` : ""}
                    </div>
                    {tdc.gastosPostCorte > 0 && (
                      <div style={{ fontSize: 9, color: P.textXs, fontFamily: FF, marginTop: 2 }}>Próximo ciclo: {fmt(tdc.gastosPostCorte)}</div>
                    )}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 17, fontWeight: 900, color: tdc.pagada ? P.ingresos.base : "#92400E", fontFamily: FF }}>{fmt(tdc.totalDeuda)}</div>
                    {tdc.pagada && <div style={{ fontSize: 9, fontWeight: 700, color: P.ingresos.base, marginTop: 2 }}>✓ Pagada</div>}
                  </div>
                  {!tdc.pagada && (
                    <button type="button" onClick={() => startPay(tdc)}
                      style={{ padding: "6px 14px", borderRadius: 10, border: "none", background: isPaying ? "#F59E0B" : alcanza ? "#143D2E" : "#DC2626", color: "#fff", fontFamily: FF, fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0, minHeight: 34 }}>
                      {isPaying ? "Cancelar" : "Pagar"}
                    </button>
                  )}
                </div>

                {isPaying && (
                  <div style={{ marginTop: 6, padding: "12px", background: "#FFFBEB", borderRadius: 12, border: `1px solid #FDE68A` }}>
                    {tdc.deudaAcumulada > tdc.totalDeudaMes && (
                      <div style={{ padding: "6px 10px", marginBottom: 10, borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", fontSize: 11, fontWeight: 600, color: "#991B1B", fontFamily: FF }}>Deuda acumulada de meses anteriores: {fmt(tdc.deudaAcumulada - tdc.totalDeudaMes)}</div>
                    )}
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#92400E", marginBottom: 8, fontFamily: FF }}>¿Qué quieres pagar?</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                      {tdc.gastosNormales > 0 && (
                        <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, background: payGastos ? "#E8F4EE" : P.surface, border: `1.5px solid ${payGastos ? "#143D2E" : P.border}`, cursor: "pointer", fontFamily: FF, fontSize: 12 }}>
                          <input type="checkbox" checked={payGastos} onChange={(e) => { setPayGastos(e.target.checked); recalcMonto(tdc, e.target.checked, payMsi); }} style={{ width: 18, height: 18, accentColor: "#143D2E" }} />
                          <div style={{ flex: 1 }}><div style={{ fontWeight: 600, color: P.text }}>Gastos normales</div><div style={{ fontSize: 10, color: P.textXs }}>{tdc.nGastos} gasto{tdc.nGastos !== 1 ? "s" : ""}</div></div>
                          <span style={{ fontWeight: 800, color: "#92400E" }}>{fmt(tdc.gastosNormales)}</span>
                        </label>
                      )}
                      {tdc.gastosNormales <= 0 && (tdc.totalExtras + tdc.totalFijos) > 0 && (
                        <div style={{ padding: "8px 12px", borderRadius: 10, background: "#E8F4EE", border: "1.5px solid #C3E0CF", fontFamily: FF, fontSize: 12, color: "#065F46", fontWeight: 600 }}>✓ Gastos normales pagados</div>
                      )}
                      {tdc.msiPendiente > 0 && (
                        <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, background: payMsi ? "#EEF0FD" : P.surface, border: `1.5px solid ${payMsi ? "#3B4FA8" : P.border}`, cursor: "pointer", fontFamily: FF, fontSize: 12 }}>
                          <input type="checkbox" checked={payMsi} onChange={(e) => { setPayMsi(e.target.checked); recalcMonto(tdc, payGastos, e.target.checked); }} style={{ width: 18, height: 18, accentColor: "#3B4FA8" }} />
                          <div style={{ flex: 1 }}><div style={{ fontWeight: 600, color: P.text }}>Meses sin intereses</div><div style={{ fontSize: 10, color: P.textXs }}>{tdc.nMsi} cuota{tdc.nMsi !== 1 ? "s" : ""}</div></div>
                          <span style={{ fontWeight: 800, color: "#3B4FA8" }}>{fmt(tdc.msiPendiente)}</span>
                        </label>
                      )}
                      {tdc.msiPendiente <= 0 && tdc.totalMsi > 0 && (
                        <div style={{ padding: "8px 12px", borderRadius: 10, background: "#EEF0FD", border: "1.5px solid #CDD3F8", fontFamily: FF, fontSize: 12, color: "#2A3880", fontWeight: 600 }}>✓ MSI pagados</div>
                      )}
                      <button type="button" onClick={() => { setPayGastos(true); setPayMsi(true); setMonto(String(tdc.totalDeuda)); }}
                        style={{ padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${payGastos && payMsi ? "#143D2E" : P.border}`, background: payGastos && payMsi ? "#143D2E" : P.surface, color: payGastos && payMsi ? "#fff" : P.textSm, fontFamily: FF, fontSize: 12, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
                        Pagar todo · {fmt(tdc.totalDeuda)}
                      </button>
                    </div>

                    <div style={{ fontSize: 10, fontWeight: 700, color: "#92400E", marginBottom: 6, fontFamily: FF }}>Pagar desde:</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                      {origenCuentas.map(c => {
                        const selected = origen === c.id;
                        return (
                          <button type="button" key={c.id} onClick={() => setOrigen(c.id)}
                            style={{ padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${selected ? "#143D2E" : P.border}`, background: selected ? "#E8F4EE" : P.surface, color: selected ? "#143D2E" : P.textSm, fontFamily: FF, fontSize: 12, fontWeight: selected ? 700 : 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, minHeight: 40 }}>
                            <span style={{ fontSize: 14 }}>{TIPO_ICON[c.tipo] || "💳"}</span>{c.nombre}
                          </button>
                        );
                      })}
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: P.bg, borderRadius: 10, marginBottom: 10 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: P.textSm, fontFamily: FF }}>Total a pagar:</span>
                      <span style={{ fontSize: 18, fontWeight: 900, color: "#92400E", fontFamily: FF }}>{fmt(Number(monto) || 0)}</span>
                    </div>

                    {saldoAlert && <div style={{ marginBottom: 8, padding: "6px 10px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", fontSize: 11, fontWeight: 600, color: "#DC2626", fontFamily: FF }}>{saldoAlert}</div>}

                    <button type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePay(tdc.id); }}
                      disabled={!origen || !Number(monto) || saving}
                      style={{ width: "100%", padding: "11px", borderRadius: 10, border: "none", background: origen && Number(monto) ? "#143D2E" : P.border, color: origen && Number(monto) ? "#fff" : P.textXs, fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: origen && Number(monto) ? "pointer" : "default", opacity: saving ? 0.6 : 1 }}>
                      {saving ? "Procesando..." : !origen ? "Selecciona cuenta origen" : !Number(monto) ? "Selecciona qué pagar" : `Pagar ${fmt(Number(monto))} a ${tdc.nombre}`}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
